import cn from 'classnames'
import { useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Menu } from 'core-components/library/menu'
import { useStaticCrud } from 'core-components/types/helpers'
import type { INetworkDebugRequest, TNetworkDebugRequestKind } from 'shared/lib/networkDebug'

import {
  formatBytes,
  formatClock,
  formatSeconds,
  sortNetworkRequests,
  type TNetworkRequestOrder,
} from './lib'
import { NetworkStatusChip } from './NetworkStatusChip'
import { NETWORK_DEBUG_TRANSLATION_KEYS } from './translations'

import styles from './NetworkRequestList.module.scss'

const KEYS = NETWORK_DEBUG_TRANSLATION_KEYS

const KIND_KEYS: Record<TNetworkDebugRequestKind, string> = {
  settings: KEYS.kindSettings,
  load: KEYS.kindLoad,
  refetch: KEYS.kindRefetch,
}

const ORDERS: { order: TNetworkRequestOrder; key: string }[] = [
  { order: 'newest', key: KEYS.orderNewest },
  { order: 'oldest', key: KEYS.orderOldest },
  { order: 'slowest', key: KEYS.orderSlowest },
  { order: 'largest', key: KEYS.orderLargest },
]

/** A pending request's bar grows with the time it has been waiting so far. */
const PENDING_SHARE = 0.4

interface INetworkRequestListProps {
  requests: INetworkDebugRequest[]
  selectedId: number | null
  onSelect: (id: number) => void
}

export const NetworkRequestList = ({
  requests,
  selectedId,
  onSelect,
}: INetworkRequestListProps) => {
  const [order, setOrder] = useState<TNetworkRequestOrder>('newest')
  const orderSource = useStaticCrud({
    data: ORDERS,
    keyField: 'order',
    displayField: 'key',
    pageSize: ORDERS.length,
  })
  const orderKey = ORDERS.find((item) => item.order === order)?.key ?? KEYS.orderNewest
  const sorted = useMemo(() => sortNetworkRequests(requests, order), [requests, order])
  const longest = Math.max(1, ...requests.map((request) => request.durationMs ?? 0))

  return (
    <div className={styles.root}>
      <div className={styles.caption}>
        <Translation
          text={KEYS.session}
          span
          replaceParams={[{ key: '{count}', value: String(requests.length) }]}
        />
      </div>
      <div className={styles.orders}>
        <Menu<(typeof ORDERS)[number], TNetworkRequestOrder>
          source={orderSource}
          caption="key"
          onChoose={setOrder}
          itemTpl={(item) => <Translation text={item.key as string} span />}
          content={() => (
            <Button
              size="sm"
              variant="outlined"
              icon="kbase kbase-omni-chevron-down"
              iconPosition="end"
            >
              <Translation text={orderKey} span />
            </Button>
          )}
        />
      </div>
      <div className={styles.list}>
        {sorted.map((request) => {
          const share = request.durationMs === null ? PENDING_SHARE : request.durationMs / longest
          const bytes = request.jsonBytes ?? request.gzipBytes

          return (
            <button
              key={request.id}
              type="button"
              className={cn(styles.item, { [styles.selected]: request.id === selectedId })}
              aria-pressed={request.id === selectedId}
              onClick={() => onSelect(request.id)}
            >
              <span className={styles.head}>
                <span className={styles.number}>{`#${request.id}`}</span>
                <span className={styles.method}>{request.method}</span>
                <span className={styles.name}>{request.name}</span>
                <NetworkStatusChip request={request} />
              </span>
              <span className={styles.sub}>
                {formatClock(request.startedAt)} ·{' '}
                <Translation text={KIND_KEYS[request.kind]} span />
                {request.silent && (
                  <>
                    {' · '}
                    <Translation text={KEYS.silent} span />
                  </>
                )}
                {request.source && ` · ${request.source}`}
              </span>
              <span className={styles.stats}>
                <span className={styles.track}>
                  <i style={{ width: `${Math.max(2, share * 100)}%` }} />
                </span>
                <span>{request.durationMs === null ? '…' : formatSeconds(request.durationMs)}</span>
                <span>{bytes === null ? '' : formatBytes(bytes)}</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

NetworkRequestList.displayName = 'NetworkRequestList'
